import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { useEffect, useState } from "react";

const navItems = [
  { path: "/admin/overview", label: "数据概览", icon: "ri-dashboard-3-line", perm: "overview" },
  { path: "/admin/clients", label: "客户管理", icon: "ri-user-settings-line", perm: "clients" },
  { path: "/admin/progress", label: "进度管理", icon: "ri-list-check-3", perm: "progress" },
  { path: "/admin/finance", label: "财务管理", icon: "ri-money-cny-circle-line", perm: "finance" },
  { path: "/admin/system", label: "系统管理", icon: "ri-settings-3-line", perm: "system" },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { admin, loading, logout, hasPermission } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!loading && !admin) {
      navigate("/admin/login");
    }
  }, [loading, admin, navigate]);

  useEffect(() => {
    if (admin?.must_change_password && location.pathname !== "/admin/change-password") {
      navigate("/admin/change-password");
    }
  }, [admin, location.pathname, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8f5f0] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-[#1a3a5c] border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-500">加载中...</p>
        </div>
      </div>
    );
  }

  if (!admin) return null;

  const filteredNavItems = navItems.filter((item) => hasPermission(item.perm));

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#f8f5f0] flex">
      {/* Mobile sidebar toggle */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 w-10 h-10 bg-[#1a3a5c] text-white rounded-lg flex items-center justify-center cursor-pointer"
      >
        <i className={sidebarOpen ? "ri-close-line" : "ri-menu-line"}></i>
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#1a3a5c] text-white flex flex-col transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        <div className="p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 overflow-hidden flex items-center justify-center p-1">
              <img
                src="/logo.png"
                alt="RTB"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <h2 className="text-sm font-bold">RTB 管理后台</h2>
              <p className="text-[10px] text-white/50">CRM 系统</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            return (
              <a
                key={item.path}
                href={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap ${
                  isActive
                    ? "bg-[#c9a96e] text-white font-medium"
                    : "text-white/70 hover:bg-white/10 hover:text-white"
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(item.path);
                  setSidebarOpen(false);
                }}
              >
                <i className={`${item.icon} text-base`}></i>
                <span>{item.label}</span>
              </a>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#c9a96e]/20 flex items-center justify-center shrink-0">
              <i className="ri-shield-user-line text-[#c9a96e]"></i>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{admin.name}</p>
              <p className="text-[10px] text-white/40">{admin.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-xs cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-r-line"></i>
            退出登录
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main content */}
      <main className="flex-1 min-w-0 flex flex-col md:pl-64">
        <div className="flex-1 p-4 md:p-8 overflow-auto">
          <Outlet />
        </div>
        <footer className="bg-white border-t border-gray-100 py-3 px-4 md:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-1">
            <p className="text-[11px] text-gray-400">
              RTB 试管助孕中心 · 客户全周期管理 CRM 系统
            </p>
            <p className="text-[10px] text-gray-300">
              &copy; 2026 RTB Fertility. All Rights Reserved.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}